import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {of} from 'rxjs';
import {catchError, map, mergeMap, switchMap} from 'rxjs/operators';
import {Configuration} from '@shared/models/configuration';
import {SnackBarService} from '@core/services/snack-bar.service';
import {InvalidateSmartGridAction} from '@shared/store/actions/smart-grid.actions';
import {FileManagerService} from '../../services/file-manager.service';
import {
  ADD_FOLDER,
  ADD_FOLDER_SUCCESS,
  AddFolderAction,
  AddFolderFailAction,
  AddFolderSuccessAction,
  DOWNLOAD_FILE,
  DownloadFileAction,
  DownloadFileFailAction,
  DownloadFileSuccessAction,
  SetCurrentLocationAction,
  UPLOAD_FILE_BY_PARENT,
  UPLOAD_FILE_BY_PARENT_SUCCESS,
  UploadFileByParentAction,
  UploadFileByParentFailAction,
  UploadFileByParentSuccessAction
} from '../actions/file-manager.actions';
import {CommandService} from '@core/services/command.service';
import {UploadFileIndex} from '@features/file-manager/file-manager-config';
import {FileMetadata} from '@shared/models/file-model';

@Injectable()
export class FileManagerEffects {
  downloadFile$ = createEffect(() => this.actions$.pipe(
    ofType(DOWNLOAD_FILE),
    switchMap((action: DownloadFileAction) => {
      this.fuseSplashScreenService.show();
      return this.fileManagerService.downloadFileWithId(action.fileId, action.sourceType)
        .pipe(
          map((payload: any) => {
            const url = window.URL.createObjectURL(payload);
            const a = document.createElement('a');
            a.setAttribute('href', url);
            a.setAttribute('target', '_blank');
            a.setAttribute('download', action.fileName);
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            this.fuseSplashScreenService.hide();
            return new DownloadFileSuccessAction(payload);
          }),
          catchError(error => {
            this.fuseSplashScreenService.hide();
            this.snackBarService.showError('File download Failed');
            return of(new DownloadFileFailAction(error));
          })
        );
    })
  ));
  addFolder$ = createEffect(() => this.actions$.pipe(
    ofType(ADD_FOLDER),
    mergeMap((action: AddFolderAction) => {
      this.fuseSplashScreenService.show();
      return this.fileManagerService.addFolder(action.addFolderModel, action.sourceType).pipe(
        map((payload) => {
          this.snackBarService.showInformation(payload.name + ' added');
          return new AddFolderSuccessAction(payload, action.sourceType, action.invalidateSubject);
        }),
        catchError(err => {
          this.snackBarService.showError(err.error);
          this.fuseSplashScreenService.hide();
          return of(new AddFolderFailAction(err));
        })
      );
    })
  ));

  uploadFileByParent$ = createEffect(() => this.actions$.pipe(
    ofType(UPLOAD_FILE_BY_PARENT),
    mergeMap((action: UploadFileByParentAction) => {
      this.fuseSplashScreenService.show();
      const fileMetaData = new FileMetadata();
      const driveId = action.options['driveId'];
      const orbitaxRepositoryDrive = action.options['orbitaxRepositoryDrive'] ?? '';
      fileMetaData.fileName = action.formData.get('fileName').toString();
      fileMetaData.driveId = driveId;
      fileMetaData.fieldValues["OrbitaxRepositoryDrive"] = orbitaxRepositoryDrive == 'true' ? true : false;
      fileMetaData.parentId = action.parentId;
      fileMetaData.publishEvent = true;
      const file = action.formData.get('file') as File;
      const uploadedFileIndex = driveId === 'root' || driveId === 'Root' ? UploadFileIndex.Shared : UploadFileIndex.Global;
      return this.fileManagerService.directUploadInS3(fileMetaData, action.formData,
        uploadedFileIndex).pipe(
        map((payload) => {
          this.snackBarService.showInformation('File uploaded');
          this.fuseSplashScreenService.hide();
          const command = payload['directUploadSuccessCommand'];
          command.fieldValues['FileSize'] = file.size;
          command.fieldValues['HasSnackBarMessage'] = false;
          if (uploadedFileIndex === UploadFileIndex.Global) {
            command.fieldValues['IsGlobalFile'] = true;
          }
          if (uploadedFileIndex === UploadFileIndex.Shared) {
            command.fieldValues['IsClientFile'] = false;
          }
          this.commandService.executePlatformCommand(command).subscribe(
            res => {
              if (res) {
                action.invalidateSubject.invalidate();
              }
            }
          );
          return new UploadFileByParentSuccessAction(action.relativePath,
            action.parentId, action.sourceType,
            action.fileName);
        }),
        catchError(err => {
          this.snackBarService.showError(err.error?.Message ?? err);
          this.fuseSplashScreenService.hide();
          return of(new UploadFileByParentFailAction(err));
        })
      );
    })
  ));
  uploadFileSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(UPLOAD_FILE_BY_PARENT_SUCCESS),
    mergeMap((action: UploadFileByParentSuccessAction) => {
      const newConfig = Configuration.getSmartGridConfig(action.sourceType);
      newConfig.Parameters.PARENT = action.parentId;
      Configuration.setSmartGridConfig(action.sourceType, newConfig);
      if (action.invalidateSubject) {
        action.invalidateSubject.invalidate();
      }
      this.fuseSplashScreenService.hide();
      return [
        new InvalidateSmartGridAction(
          action.sourceType,
          true,
          false,
          false
        ),
        new SetCurrentLocationAction(
          action.relativePath,
          action.parentId,
          action.fileName
        )
      ];
    })
  ));
  addFolderSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(ADD_FOLDER_SUCCESS),
    mergeMap((action: AddFolderSuccessAction) => {
      const newConfig = Configuration.getSmartGridConfig(action.sourceType);
      newConfig.Parameters.PARENT = action.response.id;
      if (action.invalidateSubject) {
        action.invalidateSubject.invalidate();
      } else {
        Configuration.setSmartGridConfig(action.sourceType, newConfig);
      }
      this.fuseSplashScreenService.hide();
      return [
        new InvalidateSmartGridAction(
          action.sourceType,
          true,
          false,
          false
        ),
        new SetCurrentLocationAction(
          action.response.relativePath,
          action.response.id,
          action.response.name
        )
      ];
    })
  ));

  constructor(private actions$: Actions,
              private fileManagerService: FileManagerService,
              private snackBarService: SnackBarService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private commandService: CommandService) {
  }
}


